/**
 * Fiche machine (CDC §8.2).
 *
 * Extrait de l'ancienne `AssetDetailPage.tsx` pour être réutilisable à la fois
 * en page pleine (`/machines/:id`, compatibilité des liens existants) et
 * directement dans la colonne de droite du Parc, sans navigation.
 *
 * Les compteurs restent en permanence visibles, jamais un formulaire à
 * rouvrir (Benjamin a jugé leur premier emplacement, au milieu de la pile de
 * cartes, « une usine à gaz ») : toujours en tête de la colonne de droite,
 * juste sous l'en-tête. Le futur bon d'intervention viendra s'ajouter EN
 * DESSOUS, jamais à leur place.
 *
 * Les champs techniques sont présentés à partir des DÉFINITIONS, pas des
 * valeurs : c'est ce qui rend visible ce qui manque encore. Depuis le
 * 2026-10-01, ils sont mélangés aux champs fixes dans la carte Identification
 * (une plaque signalétique ne distingue pas les deux) et se corrigent tous
 * ensemble via le formulaire « Corriger », jamais plus en ligne dans la fiche.
 * Identification reste seule dans sa colonne — devenue longue, elle ne doit
 * pas être précédée par Compteurs pour rester lisible sans défiler.
 *
 * Ce que la fiche ne montre pas encore : contrats, interventions,
 * pannes, suites, échéances et rapports tiers. Ils viendront avec leurs modules.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api, type Asset, type AssetAttribute } from '../api/client';
import { FormulaireMachine } from './FormulaireMachine';
import { Compteurs } from './Compteurs';
import { Conformite } from './Conformite';
import { GammeEntretien } from './GammeEntretien';
import { InterventionsMachine } from './InterventionsMachine';
import { SuitesMachine } from './SuitesMachine';
import { InterventionsPrevuesMachine } from './InterventionsPrevuesMachine';
import { PiecesJointes } from './PiecesJointes';
import { COMPLETUDE, STATUT_MACHINE, ordonnerCaracteristiques } from '../domaine/machines';

export function FicheMachine({
  assetId,
  onArchived,
  onUpdated,
  breadcrumb,
}: {
  assetId: string;
  onArchived?: () => void;
  onUpdated?: (machine: Asset) => void;
  breadcrumb?: ReactNode;
}) {
  const [machine, setMachine] = useState<Asset | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [detailManquantsOuvert, setDetailManquantsOuvert] = useState(false);
  /**
   * Compte les écritures faites sur les compteurs : la carte « Prochain entretien » en dépend et se
   * recalcule quand il change. Un rappel (comme `onUpdated`), pas un évènement du navigateur.
   */
  const [cleEntretien, setCleEntretien] = useState(0);
  /**
   * Dans l'autre sens : un entretien enregistré avec « relever aussi le compteur » écrit un relevé, donc la
   * carte « Compteurs » doit se relire. Le sens est unique dans chaque direction (compteurs → carte, carte →
   * compteurs), sans quoi une écriture se rappellerait elle-même en boucle.
   */
  const [cleCompteurs, setCleCompteurs] = useState(0);

  const appliquer = useCallback(
    (m: Asset) => {
      setMachine(m);
      onUpdated?.(m);
    },
    [onUpdated],
  );

  const charger = useCallback(async () => {
    try {
      appliquer(await api.asset(assetId));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.');
    }
  }, [assetId, appliquer]);

  useEffect(() => {
    setMachine(null);
    setFormulaireOuvert(false);
    setDetailManquantsOuvert(false);
    void charger();
  }, [charger]);

  const archiver = async () => {
    if (!machine) return;
    const motif = window.prompt(
      `Archiver « ${machine.label ?? machine.qrCode} » ?\n\n` +
        'La machine sort du parc mais son historique reste. Motif (facultatif) :',
    );
    if (motif === null) return;
    try {
      await api.archiveAsset(machine.id, motif || undefined);
      onArchived?.();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Archivage impossible.');
    }
  };

  if (erreur && !machine) {
    return (
      <>
        <div className="message erreur">{erreur}</div>
        {breadcrumb}
      </>
    );
  }

  if (!machine) {
    return <div className="vide">Chargement…</div>;
  }

  const etat = STATUT_MACHINE[machine.status];

  // Ordre d'affichage demandé par Benjamin (2026-10-01), de part et d'autre du
  // champ fixe « Mise en service ». Un attribut personnalisé, absent des deux
  // listes connues, s'affiche quand même — à la toute fin de la carte. Le
  // formulaire « Corriger » reprend le même ordre (`ordonnerCaracteristiques`).
  const {
    avant: attributsAvant,
    apres: attributsApres,
    restants: attributsRestants,
  } = ordonnerCaracteristiques(machine.attributes);

  return (
    <>
      {breadcrumb && <div className="fil-ariane">{breadcrumb}</div>}

      <div className="entete-page">
        <div>
          <h1>{machine.label ?? machine.model ?? 'Machine sans désignation'}</h1>
          <p>
            {machine.assetType}
            {machine.brandName && <> · {machine.brandName}</>}
            {machine.model && <> · {machine.model}</>}
          </p>
        </div>
        <div className="actions">
          {machine.reviewStatus === 'TO_VALIDATE' && <span className="etat attente">à valider</span>}
          <span className={`etat ${etat.classe}`}>{etat.texte}</span>
          <button className="secondaire" onClick={() => setFormulaireOuvert(true)}>
            Corriger
          </button>
          <button className="secondaire danger-discret" onClick={() => void archiver()}>
            Archiver
          </button>
        </div>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}

      {machine.reviewStatus === 'TO_VALIDATE' && machine.mergedIntoAssetId === null && (
        <div className="message info">
          Machine créée sur place par un technicien : le bureau doit la valider, la corriger ou dire que c’est une machine
          existante. <Link to={`/parc/a-valider?machine=${machine.id}`}>Machines à valider</Link>
        </div>
      )}
      {machine.mergedIntoAssetId !== null && (
        <div className="message info">
          Fiche en double, fusionnée : la machine est désormais suivie sur{' '}
          <Link to={`/machines/${machine.mergedIntoAssetId}`}>sa fiche</Link>. Son historique déjà écrit reste lisible ici.
        </div>
      )}
      {machine.mergedFrom.length > 0 && (
        <div className="message info">
          Fiche{machine.mergedFrom.length > 1 ? 's' : ''} fusionnée{machine.mergedFrom.length > 1 ? 's' : ''} dans celle-ci :{' '}
          {machine.mergedFrom.map((f, i) => (
            <span key={f.id}>
              {i > 0 ? ', ' : ''}
              <Link to={`/machines/${f.id}`}>{f.label ?? f.qrCode}</Link>
            </span>
          ))}
          . Leurs bons validés figurent dans « Interventions » ; leurs relevés et entretiens se lisent sur leur fiche.
        </div>
      )}

      {machine.missingCount > 0 && (
        <div className="message info">
          <button
            type="button"
            className="message-bouton"
            onClick={() => setDetailManquantsOuvert((v) => !v)}
            aria-expanded={detailManquantsOuvert}
          >
            {machine.missingCount} champ{machine.missingCount > 1 ? 's' : ''} attendu
            {machine.missingCount > 1 ? 's' : ''} reste{machine.missingCount > 1 ? 'nt' : ''} à
            renseigner. Ce n’est pas bloquant : la fiche vit avec ses trous et se complète au fil
            des passages. {detailManquantsOuvert ? '— masquer le détail' : '— voir lesquels'}
          </button>
          {detailManquantsOuvert && (
            <ul className="liste-manquants">
              {machine.attributes
                .filter((a) => a.expected && a.completeness === 'MISSING')
                .map((a) => (
                  <li key={a.definitionId}>{a.label}</li>
                ))}
            </ul>
          )}
        </div>
      )}

      <div className="deux-colonnes">
        {/* Une seule carte, identification ET caractéristiques mélangées :
            une plaque signalétique ne fait pas la distinction, l'écran ne
            doit pas non plus l'imposer (demande de Benjamin, 2026-10-01). Les
            deux viennent d'ENDROITS différents en base (colonnes fixes vs
            attributs configurables par famille) mais se corrigent ensemble,
            en un seul geste, par le bouton « Corriger » de l'en-tête. Seule
            dans sa colonne : avec les caractéristiques, la carte est longue,
            elle reste lisible sans défiler derrière Compteurs ou Implantation
            (demande de Benjamin, 2026-10-01). */}
        <div className="carte">
          <h2>Identification</h2>
          <div className="carte-corps">
            <dl className="proprietes">
              {/* Ordre choisi par Benjamin (2026-10-01) — ni l'ordre des
                  colonnes en base, ni celui des définitions de champs. */}
              <Propriete libelle="QR code">
                <code className="qr">{machine.qrCode}</code>{' '}
                <Link className="aide" to={`/machines/etiquettes?ids=${machine.id}`}>
                  Imprimer l’étiquette
                </Link>
              </Propriete>
              <Propriete libelle="Propriétaire">{machine.ownerName}</Propriete>
              <Propriete libelle="Marque">{machine.brandName}</Propriete>
              <Propriete libelle="Famille">{machine.assetType}</Propriete>
              <Propriete libelle="Modèle">{machine.model}</Propriete>
              <Propriete libelle="Numéro de série">{machine.serialNumber}</Propriete>
              <Propriete libelle="Référence">{machine.manufacturerReference}</Propriete>
              <Propriete libelle="Désignation client">{machine.clientDesignation}</Propriete>
              {attributsAvant.map((a) => (
                <ProprieteAttribut key={a.definitionId} attribut={a} />
              ))}
              <Propriete libelle="Mise en service">{machine.commissionedAt}</Propriete>
              {attributsApres.map((a) => (
                <ProprieteAttribut key={a.definitionId} attribut={a} />
              ))}
              <Propriete libelle="Fabricant">{machine.manufacturerName}</Propriete>
              {attributsRestants.map((a) => (
                <ProprieteAttribut key={a.definitionId} attribut={a} />
              ))}
            </dl>
          </div>
        </div>

        <div className="colonne-empilee">
          {/* Point 12 (non négociable) : les compteurs restent en permanence
              visibles, jamais un formulaire à rouvrir — toujours en tête de
              colonne, juste sous l'en-tête de la fiche. */}
          <Compteurs assetId={machine.id} cle={cleCompteurs} onChange={() => setCleEntretien((n) => n + 1)} />

          {/* Les derniers bons d'intervention de la machine : sous les compteurs (lot I1.1). */}
          <InterventionsMachine assetId={machine.id} />

          {/* Les suites à donner OUVERTES, nées des bons validés (lot I1.2). */}
          <SuitesMachine assetId={machine.id} />

          {/* Ce qui est déjà au planning pour cette machine, et le geste pour y mettre une visite (lot PL1a). */}
          <InterventionsPrevuesMachine machine={machine} />

          <div className="carte">
            <h2>Implantation</h2>
            <div className="carte-corps">
              <dl className="proprietes">
                <Propriete libelle="Client">{machine.customerName}</Propriete>
                <Propriete libelle="Site">
                  {machine.siteId && machine.siteName ? (
                    <Link to={`/sites/${machine.siteId}`}>{machine.siteName}</Link>
                  ) : null}
                </Propriete>
                <Propriete libelle="Ville">{machine.siteCity}</Propriete>
                <Propriete libelle="Emplacement">{machine.location}</Propriete>
                <Propriete libelle="Provenance">{machine.sourceReference}</Propriete>
              </dl>
            </div>
          </div>
        </div>
      </div>

      <Conformite assetId={machine.id} />

      <GammeEntretien
        assetId={machine.id}
        modele={machine.model}
        referenceConstructeur={machine.manufacturerReference}
        cleEntretien={cleEntretien}
        onEntretienEcrit={() => setCleCompteurs((n) => n + 1)}
      />

      <PiecesJointes ownerType="asset" ownerId={machine.id} />

      {machine.notes && (
        <div className="carte">
          <h2>Remarques</h2>
          <div className="carte-corps">{machine.notes}</div>
        </div>
      )}

      {formulaireOuvert && (
        <FormulaireMachine
          machine={machine}
          onFermer={() => setFormulaireOuvert(false)}
          onEnregistre={() => void charger()}
        />
      )}
    </>
  );
}

function Propriete({ libelle, children }: { libelle: string; children: ReactNode }) {
  const vide = children === null || children === undefined || children === '';
  return (
    <>
      <dt>{libelle}</dt>
      <dd>{vide ? <span className="aide">—</span> : children}</dd>
    </>
  );
}

/**
 * Une caractéristique, en lecture — mélangée aux champs fixes dans la carte
 * Identification (demande de Benjamin, 2026-10-01 : une plaque signalétique
 * ne distingue pas identification et caractéristiques, l'écran de lecture non
 * plus). La saisie se fait désormais uniquement via le formulaire « Corriger »,
 * qui connaît aussi ces attributs — jamais plus en ligne ici.
 */
function ProprieteAttribut({ attribut }: { attribut: AssetAttribute }) {
  const etat = COMPLETUDE[attribut.completeness];
  return (
    <Propriete libelle={attribut.label}>
      {attribut.completeness === 'PROVIDED' ? (
        <>
          {attribut.value}
          {attribut.unit && <span className="aide"> {attribut.unit}</span>}
        </>
      ) : (
        <span className={`etat ${etat.classe}`} title={attribut.unavailableReason ?? undefined}>
          {etat.texte}
        </span>
      )}
    </Propriete>
  );
}
